import { isAfter, isBefore } from 'date-fns';

import { type DayOfWeek } from '@blaze-shared/api-contracts-domain';
import { type DatasetRow } from '@blaze-shared/schema-domain';
import { regexpMatch, removeSeconds, timeFormatter, timeRegex } from '@blaze-shared/shared-util';

import {
  type AvailabilityScheduleConfig,
  type AvailabilityScheduleRule,
} from './availability.model';

export interface DataPropsMapping {
  id: string;
  startDate: string;
  endDate: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
}
export interface DataPropsValues {
  id?: string;
  startDate: string | null;
  endDate: string | null;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

type AvailabilityScheduleBuild = Omit<AvailabilityScheduleConfig, 'rules'> & {
  rules: Partial<Record<DayOfWeek, AvailabilityScheduleRule>>;
};

const INT_DOW_MAP: Record<number, DayOfWeek> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};
const DOW_INT_MAP: Record<DayOfWeek, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

export function datasetRowsToModel(
  data: DatasetRow[] | undefined,
  propsMappings: DataPropsMapping,
): AvailabilityScheduleConfig[] {
  const schedules: Record<string, AvailabilityScheduleBuild> = (data ?? []).reduce(
    (result, dataRow) => {
      const entry = getDataEntry(dataRow, propsMappings);
      const day = INT_DOW_MAP[entry.dayOfWeek];
      const key = `${entry.startDate ?? '-'}:${entry.endDate ?? '-'}`;
      let schedule = result[key];
      if (!schedule) {
        schedule = result[key] = {
          id: key,
          rules: {},
          since: entry.startDate,
          until: entry.endDate,
          status: 'disabled',
        };
      }
      let rule = schedule.rules[day];
      if (!rule) {
        rule = schedule.rules[day] = { enabled: true, day, timeSlots: [] };
      }
      rule.timeSlots.push({
        from: entry.startTime,
        to: entry.endTime,
        dataRowId: dataRow[propsMappings.id],
      });
      return result;
    },
    {},
  );

  const results: AvailabilityScheduleConfig[] = Object.values(schedules).map((s) => ({
    ...s,
    rules: Object.values(s.rules),
  }));
  if (!results.length) {
    results.push({ id: '-:-', rules: [], since: null, until: null, status: 'active' });
  }
  return results;
}

const SCHEDULE_STATUS_SORT = {
  active: 0,
  upcoming: 1,
  expired: 2,
  disabled: 3,
};

export function updateSchedulesStatuses(
  schedules: AvailabilityScheduleConfig[],
  today?: Date,
): AvailabilityScheduleConfig[] {
  if (!schedules.length) {
    return schedules;
  }
  const todayStr = transformStringDateToDate(prepareDateToOutput(today ?? new Date()));

  const resultSchedules: AvailabilityScheduleConfig[] = schedules.map((schedule) => {
    if (schedule.isNew) {
      return { ...schedule, status: 'disabled' };
    }

    if (!schedule.since) {
      // has no start & ended before today
      if (schedule.until && isBefore(schedule.until, todayStr)) {
        return { ...schedule, status: 'expired' };
      }
      // has no start & (no until | until is today+)
      return { ...schedule, status: 'active' };
    }

    // starts later than today
    if (isAfter(transformStringDateToDate(schedule.since), todayStr)) {
      return { ...schedule, status: 'upcoming' };
    }
    // doesn't expire or ends later than today
    if (!schedule.until || !isBefore(transformStringDateToDate(schedule.until), todayStr)) {
      return { ...schedule, status: 'active' };
    }

    return { ...schedule, status: 'expired' };
  });

  resultSchedules.sort((x, y) => SCHEDULE_STATUS_SORT[x.status] - SCHEDULE_STATUS_SORT[y.status]);
  return resultSchedules;
}

export function modelToDatasetRows(
  schedules: AvailabilityScheduleConfig[],
  propsMappings: DataPropsMapping,
  data: DatasetRow[],
  emptyRow: DatasetRow,
): DatasetRow[] {
  const rows: DatasetRow[] = [];
  const dataMap = data.reduce(
    (acc, row) => {
      acc[row[propsMappings.id]] = row;
      return acc;
    },
    {} as Record<string, DatasetRow>,
  );
  listExistingDataRows(dataMap, rows, schedules, emptyRow, propsMappings);
  listDeletedDataRows(dataMap, rows);
  return rows;
}

function listExistingDataRows(
  dataMap: Record<string, DatasetRow>,
  rows: DatasetRow[],
  schedules: AvailabilityScheduleConfig[],
  emptyRow: DatasetRow,
  propsMappings: DataPropsMapping,
): void {
  for (const schedule of schedules) {
    if (schedule.isNew) {
      continue;
    }
    for (const rule of schedule.rules) {
      if (!rule.enabled) {
        continue;
      }
      for (const ts of rule.timeSlots) {
        const dataRow = ts.dataRowId ? dataMap[ts.dataRowId] : emptyRow;
        if (ts.dataRowId) {
          delete dataMap[ts.dataRowId];
        }
        rows.push({
          ...dataRow,
          [propsMappings.startDate]: schedule.since,
          [propsMappings.endDate]: schedule.until,
          [propsMappings.dayOfWeek]: DOW_INT_MAP[rule.day],
          [propsMappings.startTime]: ts.from,
          [propsMappings.endTime]: ts.to,
          _isDeleted: false,
        });
      }
    }
  }
}

function listDeletedDataRows(dataMap: Record<string, DatasetRow>, rows: DatasetRow[]): void {
  for (const deleted of Object.values(dataMap)) {
    rows.push({ ...deleted, _isDeleted: true });
  }
}

function getDataEntry(row: DatasetRow, propsMappings: DataPropsMapping): DataPropsValues {
  return {
    id: propsMappings.id ? row[propsMappings.id] : null,
    startDate: propsMappings.startDate ? row[propsMappings.startDate] : null,
    endDate: propsMappings.endDate ? row[propsMappings.endDate] : null,
    dayOfWeek: propsMappings.dayOfWeek ? row[propsMappings.dayOfWeek] : null,
    startTime: propsMappings.startTime ? row[propsMappings.startTime] : null,
    endTime: propsMappings.endTime ? row[propsMappings.endTime] : null,
  };
}

export function sortDays(days: DayOfWeek[] | undefined, weekStart?: DayOfWeek): DayOfWeek[] {
  if (!days) {
    return [];
  }
  const sorted: DayOfWeek[] = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  weekStart ??= 'Sunday';
  if (weekStart === 'Monday') {
    sorted.shift();
    sorted.push('Sunday');
  }

  return sorted.filter((d) => days.includes(d));
}

export function validateSchedule(
  schedules: AvailabilityScheduleConfig[],
  updated: AvailabilityScheduleConfig,
): {
  ruleErrors: Record<string, string>;
  slotsErrors: string[][];
} {
  const noSinceExists = schedules.find((s) => s.id !== updated.id && !s.isNew && !s.since);
  const noUntilExists = schedules.find((s) => s.id !== updated.id && !s.isNew && !s.until);
  const ruleErrors: Record<string, string> = {};

  if (noSinceExists && !updated.since) {
    ruleErrors['since'] = '* Since is required';
  }
  if (noUntilExists && !updated.until) {
    ruleErrors['until'] = '* Until is required';
  } else if (updated.since && updated.until && updated.until < updated.since) {
    ruleErrors['until'] = 'Until must be after Since';
  }

  validateScheduleNoOverlaps(ruleErrors, updated, schedules);

  const slotsErrors: string[][] = validateScheduleSlots(updated);

  return { slotsErrors, ruleErrors };
}

function validateScheduleNoOverlaps(
  ruleErrors: Record<string, string>,
  updated: AvailabilityScheduleConfig,
  schedules: AvailabilityScheduleConfig[],
): void {
  if (!ruleErrors['since'] && !ruleErrors['until']) {
    for (let i = 0; i < schedules.length; i++) {
      const sh = schedules[i];
      if (sh.id === updated.id) {
        continue;
      }
      let hasOverlap = false;

      const updatedStartsBeforeShEnds = !sh.until || !updated.since || updated.since < sh.until;
      const shStartsBeforeUpdatedEnds = !updated.until || !sh.since || sh.since < updated.until;
      hasOverlap = updatedStartsBeforeShEnds && shStartsBeforeUpdatedEnds;

      if (hasOverlap) {
        ruleErrors['until'] = `Overlap with schedule #${i + 1}`;
      }
    }
  }
}

function validateScheduleSlots(updated: AvailabilityScheduleConfig): string[][] {
  const slotsErrors: string[][] = [];
  for (let ruleIdx = 0; ruleIdx < updated.rules.length; ruleIdx++) {
    const rule = updated.rules[ruleIdx];
    if (!rule.enabled) {
      continue;
    }
    let previousSlot = undefined;
    for (let i = 0; i < rule.timeSlots.length; i++) {
      const slot = rule.timeSlots[i];
      const slotFrom = formatTime(slot.from);
      const slotTo = formatTime(slot.to);
      let error = undefined;
      if (!slotFrom) {
        error = 'Start time is required';
      } else if (!slotTo) {
        error = 'End time is required';
      } else if (slotTo <= slotFrom) {
        error = 'Start time must be before End time';
      } else if (previousSlot && slotFrom < formatTime(previousSlot.to)) {
        error =
          slotTo > formatTime(previousSlot.from)
            ? 'Start time overlaps previous slot'
            : 'Slot must not be before the previous one';
      }
      if (error) {
        if (!slotsErrors[ruleIdx]) {
          slotsErrors[ruleIdx] = [];
        }
        slotsErrors[ruleIdx][i] = error;
      }
      previousSlot = slot;
    }
  }
  return slotsErrors;
}

function formatTime(time: string | null): string {
  if (!time) {
    return '';
  }
  if (time.includes(':')) {
    if (time.length === 5) {
      return time;
    } else if (time.length < 5) {
      const [h, m] = time.split(':');
      return `${('00' + h).substr(-2, 2)}:${('00' + m).substr(-2, 2)}`;
    }
  }
  return time;
}

export function getTimeValueToDisplay(value: unknown, hour12: boolean): string | null {
  if (value === null || typeof value !== 'string' || !regexpMatch(timeRegex, value)) {
    return null;
  }

  return removeSeconds(timeFormatter(value, hour12)) || null;
}

export function prepareDateToOutput(date: string | Date): string {
  if (typeof date === 'string') {
    return date.split('T')[0];
  }
  const dateDate = date.getDate().toString().padStart(2, '0');
  const dateMonth = (date.getMonth() + 1).toString().padStart(2, '0');
  const dateYear = date.getFullYear().toString().padStart(4, '0');
  const valueToEmit = `${dateYear}-${dateMonth}-${dateDate}`;
  return valueToEmit;
}

export function transformStringDateToDate(value: string): Date {
  const tzOffset = new Date(value + 'T00:00:00').getTimezoneOffset();
  const tzOffsetHours = Math.floor(tzOffset / 60)
    .toString()
    .padStart(2, '0');
  const tzOffsetMinutes = (tzOffset % 60).toString().padStart(2, '0');

  return new Date(value + (tzOffset > 0 ? `T${tzOffsetHours}:${tzOffsetMinutes}:00` : 'T00:00:00'));
}
